import { bind, filter, find, flatten, get, map, range, trim } from 'lodash-es';

import * as dragula from 'dragula';

interface IElement {
  dataset: {
    headerName: string;
    columnName: string;
  };
}

interface IMapping {
  headerName: string;
  columnName: string;
  columnHeader?: string;
  mapFieldName?: string;
}

// Be sure to pass it elements that contain a headerName (i.e. header elements)
let getHeaderMapping = (el: IElement, mappings: IMapping[]) =>
  find(mappings, (mapping) => mapping.headerName == el.dataset.headerName);

// Be sure to pass it elements that contain a columnName (i.e. tier column elements)
let getColumnMapping = (el: IElement, mappings: IMapping[]) =>
  find(mappings, (mapping) => mapping.columnName == el.dataset.columnName);

let removeMapping = (mapping, mappings) => {
  if (mapping) {
    let index = mappings.indexOf(mapping);
    mappings.splice(index, 1);
  }
};

let isHeaderDragDropZone = (target) =>
  target && target.className && target.className.includes('header-drop-zone');

let isValidColumnDragZone = (target) =>
  target && target.className && target.className.includes('column-drop-zone');

let isValidColumnDropZone = (target) =>
  target && target.className && target.className.includes('column-drop-zone');

let dragulaConfig = {
  isContainer: (el) => isHeaderDragDropZone(el) || isValidColumnDragZone(el),
  accepts: (el, target, source, sibling) =>
    isHeaderDragDropZone(target) || isValidColumnDropZone(target),
  revertOnSpill: true,
  removeOnSpill: false
};

// TODO: Write tests for this and move somewhere reusable
// Get an array of proxy names we can use when the columns are not given names. There are ~700 such names using single
// and double letter combos.
const getAlphaRange = (startingChar, endingChar) => {
  let startCharCode = startingChar.charCodeAt(0);
  let endCharCode = endingChar.charCodeAt(0);
  let charIndexRange = range(
    startCharCode,
    Math.min(startCharCode + 26, endCharCode + 1)
  );

  // The initial set is from A-Z.
  let proxyNames = map(charIndexRange, (charIndex) =>
    String.fromCharCode(charIndex)
  );

  // The next set is from AA-ZZ.
  let doubleLetterProxyNames = flatten(
    map(proxyNames, (proxyName) => {
      // Generate proxyName + A, proxyName + B... and return them all as an array.
      return map(proxyNames, (proxy) => {
        return proxyName + proxy;
      });
    })
  );

  // Combine the list of single and double letter proxy names.
  proxyNames.push(...doubleLetterProxyNames);

  return proxyNames;
};

// Called with an array of default values
// Returns a function that matches each empty item in a list to the corresponding default
const setDefaultValuesForList = (defaultValues) => (row) => {
  return map(row, (col: any = '', index) =>
    trim(col) ? col : defaultValues[index]
  );
};

const addDefaultValuesForEmptyHeaders = (headerRows) => {
  // When we have "rows" (which are not rows at all, they're columns within a given row) which have no label, we
  // have to come up with one.
  let defaultValues = getAlphaRange('A', 'Z').map((val) => 'Column ' + val);
  let defaultValuesMapper = setDefaultValuesForList(defaultValues);

  const headers = headerRows.map(defaultValuesMapper);

  return headers;
};

class SelectColumnsPaneCtrl {
  static bindings = {
    offer: '<',
    uploadApi: '<'
  };

  offer;
  uploadApi;

  state;

  selectedWorksheet;
  headers;

  drake;
  unsubscribeFromStore;

  headerRows;

  mappings = [];

  constructor(private UploadModel, private api, private $scope) {
    'ngInject';
  }

  $onInit() {
    this.updateState();

    this.unsubscribeFromStore = this.UploadModel.store.subscribe(() => {
      // Update angularjs if the state is changed via redux debugger
      // this.$scope.$applyAsync(() => {
      this.updateState();

      // The user may get here before the file has finished parsing. If so, load the headers when that's done
      if (
        !this.headerRows &&
        get(this.state.file, 'isUploaded') &&
        !get(this.selectedWorksheet, 'headers.isLoaded')
      ) {
        this.loadHeaderRows();
      }

      // Once the file is uploaded, load the columns if empty and not already loaded and not in the process of loading
      if (
        !get(this.state.columns, 'data') &&
        get(this.state.file, 'isUploaded') &&
        !get(this.state.columns, 'isLoaded') &&
        !get(this.state.columns, 'isLoading')
      ) {
        this.loadPricingFields();
      }
      // });
    });

    if (
      get(this.state.file, 'isUploaded') &&
      !get(this.state.columns, 'isLoaded')
    ) {
      this.loadPricingFields();
    }

    // If headers have already been loaded, add them, otherwise load them if we have a worksheet
    if (get(this.selectedWorksheet, 'headers.isLoaded')) {
      this.headerRows = addDefaultValuesForEmptyHeaders(
        this.selectedWorksheet.summaryData
      );
    } else if (!this.headerRows && get(this.state.file, 'isUploaded')) {
      this.loadHeaderRows();
    }
  }

  $onDestroy() {
    if (this.unsubscribeFromStore) {
      this.unsubscribeFromStore();
    }

    if (this.drake) {
      this.drake.destroy();
    }
  }

  loadPricingFields() {
    let loadOfferColumnsAction = this.UploadModel.loadOfferColumns();
    this.UploadModel.store.dispatch(loadOfferColumnsAction);

    // TODO: Move this into the loadOfferColumns action once we add redux-thunk
    let pricingFieldsLoader = this.uploadApi.getPricingFields({
      offerId: this.offer ? this.offer.id : null,
      fileUploadId: this.state.file ? this.state.file.uploadId : null
    });

    pricingFieldsLoader.then((columns) => {
      let offerColumnsLoadedAction = this.UploadModel.offerColumnsLoaded(
        columns
      );
      this.UploadModel.store.dispatch(offerColumnsLoadedAction);
    });
  }

  updateState() {
    this.state = this.UploadModel.store.getState();
    this.selectedWorksheet = this.UploadModel.getSelectedWorksheet(this.state);
    this.mappings = [].concat(get(this.selectedWorksheet, 'columnMappings'));
  }

  onDropHandler(el, target) {
    // Cancel the default dragula drop option and handle it ourselves
    this.drake.cancel(true);

    // Remove any existing header or column mappings
    removeMapping(getHeaderMapping(el, this.mappings), this.mappings);
    removeMapping(getColumnMapping(target, this.mappings), this.mappings);

    if (el.dataset.headerName && target.dataset.columnName) {
      this.mappings.push({
        headerName: el.dataset.headerName,
        columnName: target.dataset.columnName
      });
    }

    let action = this.UploadModel.updateWorksheetColumnMappings(
      this.selectedWorksheet,
      this.mappings
    );
    this.UploadModel.store.dispatch(action);

    // Dragula events happen outside the angular lifecycle, so notify the view that we updated the model
    this.$scope.$digest();
  }

  onRenderHeaders() {
    this.drake = dragula(dragulaConfig).on(
      'drop',
      bind(this.onDropHandler, this)
    );
  }

  loadHeaderRows() {
    // The backend requires an upload id due to the api path
    // It's not necessary, so we could just pass null... but I'm passing it in case they end up using it in the future
    if (
      get(this.state.file, 'uploadId') &&
      !get(this.selectedWorksheet, 'headers.isLoading')
    ) {
      let action = this.UploadModel.fetchWorksheetHeaders(
        this.selectedWorksheet,
        get(this.state.file, 'uploadId')
      );
      this.UploadModel.store.dispatch(action).then(() => {
        this.headerRows = addDefaultValuesForEmptyHeaders(
          get(this.selectedWorksheet, 'summaryData')
        );
      });
    }
  }

  updateColumnMappings(rowIndex) {
    // If headers haven't been loaded yet, there is nothing to map
    if (!get(this.selectedWorksheet, 'headers')) {
      return;
    }

    let rows = get(this.selectedWorksheet.headers, 'rows');
    let mappings: IMapping[] = rows[rowIndex];

    // We only care about the mappings we can use
    // the backend gives us null mappings for columns that are null - Kavya is planning to filter these out soon
    mappings = filter(mappings, (mapping) => {
      return mapping.columnHeader != null && mapping.mapFieldName != null;
    });

    mappings = map(mappings, (mapping) => {
      return {
        headerName: mapping.columnHeader,
        columnName: mapping.mapFieldName
      };
    });

    let action = this.UploadModel.updateWorksheetColumnMappings(
      this.selectedWorksheet,
      mappings
    );
    this.UploadModel.store.dispatch(action);
  }

  onRowIndexChange($event) {
    if ($event.rowIndex !== null) {
      let action = this.UploadModel.updateWorksheetRowIndex(
        this.selectedWorksheet,
        $event.rowIndex
      );
      this.UploadModel.store.dispatch(action);
    }
  }

  onHeaderRowChange($event) {
    if ($event.rowIndex !== null) {
      this.updateColumnMappings($event.rowIndex);
    }
  }
}

export const SelectColumnsPaneComponent = {
  template: require('./selectColumnsPane.component.html'),
  controller: SelectColumnsPaneCtrl,
  bindings: SelectColumnsPaneCtrl.bindings,
  transclude: {
    dropColumnDescription: '?dropColumnDescription'
  }
};
